import React, { ReactNode } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import { createAuthStore, selectAuthState } from './store/store'
import { AuthHandlers } from './types.d'
import { initializeState } from './store'
import { useAuthCounter } from './hooks/useAuthCounter'

// Extend the Window interface to include wouldHavePresentedUnloadAlert
declare global {
  interface Window {
    wouldHavePresentedUnloadAlert?: () => void
    __bypassUnloadWarning?: boolean
  }
}

type AuthFlowProviderProps = {
  handlers: AuthHandlers
  children: ReactNode
  recaptchaSiteKey?: string | null
  logoUrl: string
  clubId: string
  initialError?: string | null
  csrfToken: string
  loginHint?: string
  forcePkEnroll?: boolean
  captchaBypass?: boolean
  customDomain?: string | null
}

export function AuthFlowProvider({
  handlers,
  recaptchaSiteKey,
  initialError,
  logoUrl,
  clubId,
  csrfToken,
  loginHint,
  forcePkEnroll,
  captchaBypass,
  customDomain,
  children,
}: AuthFlowProviderProps) {
  // Create auth counter for thunk cancellation
  const { counterRef } = useAuthCounter()

  // Create Redux store with handlers and counter ref injected
  const store = React.useMemo(
    () => createAuthStore(handlers, counterRef),
    [handlers, counterRef],
  )
  const [isInitialized, setIsInitialized] = React.useState(false)

  // Initialize the store with props
  React.useEffect(() => {
    store.dispatch(
      initializeState({
        config: {
          recaptchaSiteKey,
          logoUrl,
          clubId,
          csrfToken,
          loginHint,
          forcePkEnroll,
          captchaBypass,
          customDomain,
          returnUrl: '/', // Default value
        },
        error: initialError,
      }),
    )
    setIsInitialized(true)
  }, [
    store,
    recaptchaSiteKey,
    logoUrl,
    clubId,
    csrfToken,
    loginHint,
    forcePkEnroll,
    captchaBypass,
    initialError,
    customDomain,
  ])

  // Handle beforeunload warning for unsaved changes
  React.useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Skip warning if intentional navigation via Navigation.navigate
      if (window.__bypassUnloadWarning) {
        return
      }

      const state = selectAuthState(store.getState())
      if (state.isDirty) {
        event.preventDefault()

        // enable testing
        if (typeof window.wouldHavePresentedUnloadAlert === 'function') {
          window.wouldHavePresentedUnloadAlert()
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [store])

  if (!isInitialized) {
    return null // Or a loading spinner
  }

  return <ReduxProvider store={store}>{children}</ReduxProvider>
}
